import {useQuery} from "@apollo/client";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {FiZoomIn} from "react-icons/fi";
import {Location, useLocation} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {
  Last24Hours,
  Last30Days,
} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {useNavigationBlocker} from "@/lib/hooks/useNavigationBlocker.tsx";
import {
  GQL_METRICS_EXPLORER_DOWNTIME,
  GQL_METRICS_EXPLORER_QUERY,
  GQL_METRICS_EXPLORER_SCHEMA,
  MetricsExplorerDowntimeData,
  MetricsExplorerQueryData,
  MetricsExplorerSchemaData,
} from "@/lib/queries/metricsExplorer.ts";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerView} from "@/lib/types/metricsExplorer.ts";
import {MetricsExplorerActions} from "@/pages/metricsExplorer/actions/MetricsExplorerActions.tsx";
import {AnalysisControls} from "@/pages/metricsExplorer/analysis/AnalysisControls.tsx";
import {AnalysisReadout} from "@/pages/metricsExplorer/analysis/AnalysisReadout.tsx";
import {applyCatalogDefaults, GuessedField} from "@/pages/metricsExplorer/catalogDefaults.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {buildDowntimeWindows} from "@/pages/metricsExplorer/chartShapes.ts";
import {
  createDefaultConfig,
  ExplorerConfig,
  ExplorerSeriesConfig,
  singleCheckId,
  toAnalysisInput,
  isSeriesQueryable,
  seriesLetters,
  toQuerySpecs,
} from "@/pages/metricsExplorer/explorerConfig.ts";
import {MetricsExplorerChart} from "@/pages/metricsExplorer/MetricsExplorerChart.tsx";
import {MetricsExplorerTitle} from "@/pages/metricsExplorer/MetricsExplorerTitle.tsx";
import {ChartNameFormValues} from "@/pages/metricsExplorer/savedViews/ChartNameForm.tsx";
import {ChartNameMenu} from "@/pages/metricsExplorer/savedViews/ChartNameMenu.tsx";
import {ChartNameMode, ChartNameModal} from "@/pages/metricsExplorer/savedViews/ChartNameModal.tsx";
import {useSavedViews} from "@/pages/metricsExplorer/savedViews/useSavedViews.ts";
import {SeriesBuilderRail} from "@/pages/metricsExplorer/seriesBuilder/SeriesBuilderRail.tsx";
import {ChartSummary} from "@/pages/metricsExplorer/toolbar/ChartSummary.tsx";
import {MetricsExplorerToolbar} from "@/pages/metricsExplorer/toolbar/MetricsExplorerToolbar.tsx";
import {useExplorerConfig} from "@/pages/metricsExplorer/useExplorerConfig.ts";

import styles from "./MetricsExplorerContent.module.scss";

const RANGE_PARAMS = {fromKey: "start", toKey: "end", fixedRangeKey: "range"} as const;

export const MetricsExplorerContent = () => {
  const {
    config,
    setConfig,
    requestedViewId,
    adoptRequestedView,
    dismissRequestedView,
    updateSeries,
    addSeries,
    removeSeries,
    duplicateSeries,
  } = useExplorerConfig();
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    ...RANGE_PARAMS,
    withDefault: Last24Hours,
  });
  // Read once: every config change rewrites the URL, and a navigation without state drops it,
  // which happens on the very first render when catalog defaults are filled in.
  const location = useLocation();
  const [backTo] = useState<Location | undefined>(() => location.state?.from);

  // Check names and types are needed to label series and to narrow the catalog, but the
  // config only stores ids. The pickers hand back the full records as they resolve them.
  const [checkMeta, setCheckMeta] = useState<Record<number, CheckInfo>>({});
  const handleChecksResolved = useCallback((checks: CheckInfo[]) => {
    setCheckMeta((previous) => {
      const next = {...previous};
      checks.forEach((check) => {
        next[check.id] = check;
      });
      return next;
    });
  }, []);

  const {data: schemaData} = useQuery<MetricsExplorerSchemaData>(GQL_METRICS_EXPLORER_SCHEMA, {
    // The catalog only changes when metric_defs.yaml is deployed.
    fetchPolicy: "cache-first",
  });

  // Picking or switching a check should draw something straight away rather than leaving
  // more dropdowns to work through or an error to read, and a preset link deliberately
  // arrives without them. Fields filled by a guess among several options stay flagged until
  // the user sets them.
  const schema = schemaData?.metricsExplorerSchema;
  const [guessedFields, setGuessedFields] = useState<Record<string, GuessedField[]>>({});
  // The chart as it stood once opened and filled in, before the user changed anything. A
  // chart that was never saved is unsaved only when it differs from this, so opening the
  // explorer from a check and going straight back asks nothing.
  const [openedConfig, setOpenedConfig] = useState<ExplorerConfig | null>(null);
  useEffect(() => {
    const {config: filled, guessed} = applyCatalogDefaults(config, schema, checkMeta);
    if (filled !== config) {
      setGuessedFields((previous) => ({...previous, ...guessed}));
      setConfig(filled);
      return;
    }
    // Settled only once the catalog has had its say, which needs every check's type.
    const isSettled = schema && config.series.every((series) => series.checkIds.every((id) => checkMeta[id]));
    if (openedConfig === null && isSettled) {
      setOpenedConfig(config);
    }
  }, [config, schema, checkMeta, setConfig, openedConfig]);

  const handleUpdateSeries = useCallback(
    (ref: string, patch: Partial<ExplorerSeriesConfig>) => {
      updateSeries(ref, patch);
      setGuessedFields((previous) => {
        const remaining = (previous[ref] ?? []).filter((field) => !(field in patch));
        return {...previous, [ref]: remaining};
      });
    },
    [updateSeries]
  );

  const specs = useMemo(() => toQuerySpecs(config), [config]);

  const chartRef = useRef<HTMLDivElement | null>(null);
  const queryInput = useMemo(
    () =>
      specs.length === 0
        ? null
        : {
            start_date: dateRange.from.toISOString(),
            end_date: dateRange.to.toISOString(),
            granularity: config.granularity,
            series: specs,
            comparison: config.comparison ? {period: config.comparison} : null,
            analysis: toAnalysisInput(config.analysis),
          },
    [specs, dateRange.from, dateRange.to, config.granularity, config.comparison, config.analysis]
  );

  const {data, loading, error} = useQuery<MetricsExplorerQueryData>(GQL_METRICS_EXPLORER_QUERY, {
    skip: queryInput === null,
    variables: {query: queryInput},
  });

  // Downtime belongs to one check, so the overlay is only offered when the chart is about
  // one; a band on a multi-check chart would not say which series it applied to.
  const downtimeCheckId = singleCheckId(config);
  const {data: downtimeData} = useQuery<MetricsExplorerDowntimeData>(GQL_METRICS_EXPLORER_DOWNTIME, {
    skip: !config.showDowntime || downtimeCheckId === null,
    variables: {
      id: downtimeCheckId,
      start: dateRange.from.toISOString(),
      end: dateRange.to.toISOString(),
    },
  });
  const downtime = useMemo(
    () => buildDowntimeWindows(downtimeData?.stats.check_state_events ?? [], dateRange.to),
    [downtimeData, dateRange.to]
  );

  const {views, loading: viewsLoading, saveView, deleteView, configOf} = useSavedViews(true);
  const [activeView, setActiveView] = useState<MetricsExplorerView | null>(null);
  const [nameDialog, setNameDialog] = useState<{mode: ChartNameMode; defaultName: string} | null>(null);

  const openView = useCallback(
    (view: MetricsExplorerView) => {
      const saved = configOf(view);
      if (!saved) {
        toast.error(`"${view.name}" was saved in a format this page no longer understands.`);
        return;
      }
      setActiveView(view);
      setConfig(saved);
    },
    [configOf, setConfig]
  );

  // A dashboard widget links here by saved-chart id. Resolve it once the list arrives and
  // swap it for a real config in the URL, so from that point the page is indistinguishable
  // from any other shared explorer link. Waiting on `viewsLoading` is what separates "no
  // charts yet" from "not known yet" — without it an empty first render looks like a
  // missing chart.
  useEffect(() => {
    if (requestedViewId === null || viewsLoading) {
      return;
    }
    const requested = views.find((item) => item.id === requestedViewId);
    if (!requested) {
      toast.error("That chart is no longer available.");
      dismissRequestedView();
      return;
    }
    const saved = configOf(requested);
    if (!saved) {
      toast.error(`"${requested.name}" was saved in a format this page no longer understands.`);
      dismissRequestedView();
      return;
    }
    setActiveView(requested);
    adoptRequestedView(saved);
  }, [requestedViewId, viewsLoading, views, configOf, adoptRequestedView, dismissRequestedView]);

  const commitSave = useCallback(
    async (name: string, id?: number) => {
      const saved = await saveView(name, config, id);
      if (saved) {
        setActiveView(saved);
      }
    },
    [saveView, config]
  );

  const handleNameSubmit = async ({name}: ChartNameFormValues) => {
    const mode = nameDialog?.mode;
    setNameDialog(null);
    if (mode !== "rename") {
      await commitSave(name);
      return;
    }
    // Renaming keeps the chart as stored; edits on screen are not saved along with the new name.
    const stored = activeView && configOf(activeView);
    if (activeView && stored) {
      const renamed = await saveView(name, stored, activeView.id);
      if (renamed) {
        setActiveView(renamed);
      }
    }
  };

  // Compared as values: the stored config comes back from a jsonb column, which does not keep
  // key order. A chart never saved counts as unsaved once there is something to save.
  const savedConfig = useMemo(() => (activeView ? configOf(activeView) : null), [activeView, configOf]);
  // Hidden series are saved like any other, so what can be saved is every complete series,
  // not only those being queried.
  const hasSomethingToSave = config.series.some(isSeriesQueryable);
  const isAllHidden = hasSomethingToSave && specs.length === 0;
  const isUnsaved = activeView
    ? !isEqual(config, savedConfig)
    : hasSomethingToSave && openedConfig !== null && !isEqual(config, openedConfig);

  // Leaving the page with unsaved edits asks first, as check creation does. The explorer keeps
  // the chart in the URL, so its own edits are navigations on the same path and pass through.
  const {NavigationBlockerModal} = useNavigationBlocker({shouldBlock: isUnsaved, allowSamePathNavigation: true});

  const result = data?.metricsExplorerQuery;
  const checkNames = useMemo(
    () => Object.fromEntries(Object.values(checkMeta).map((check) => [check.id, check.name])) as Record<number, string>,
    [checkMeta]
  );

  // Only a query that ran and came back empty is "no data". Before a check and a metric are
  // picked nothing is queried, and the charts keep saying what is missing.
  const hasNoData = result?.meta.total_datapoints === 0;
  const letters = useMemo(() => seriesLetters(config), [config]);
  // A single series needs no letter to tell its lines apart.
  const legendLetters = config.series.length > 1 ? letters : undefined;
  const checkCount = new Set(config.series.flatMap((series) => series.checkIds)).size;
  const now = new Date();
  // Offering the last 30 days only helps when the window on screen does not already cover them.
  const coversLast30Days = dateRange.from <= Last30Days.getStartDate(now) && dateRange.to >= now;

  const showLast30Days = () =>
    // A fixed interval fine enough for the old window can exceed the bucket cap over 30 days
    // and fail the whole request, so the server is left to pick one that fits.
    setConfig(
      {...config, granularity: "AUTO"},
      {
        [RANGE_PARAMS.fixedRangeKey]: Last30Days.value,
        [RANGE_PARAMS.fromKey]: null,
        [RANGE_PARAMS.toKey]: null,
      }
    );

  // Named after what is drawn, so a chart of one metric reads "Response time".
  const chartTitle = Array.from(
    new Set((result?.series ?? []).filter((item) => item.kind === "PRIMARY").map((item) => item.label))
  ).join(", ");

  let chartBody: ReactNode = null;
  if (isAllHidden) {
    chartBody = <ChartEmptyState title="All series are hidden" message="Show a series with its eye icon to draw it." />;
  } else if (loading && !result) {
    chartBody = <LoadingContainer text="Querying metrics…" />;
  } else if (hasNoData) {
    chartBody = (
      <ChartEmptyState
        title={`No data for ${checkCount > 1 ? "these checks" : "this check"} in this range`}
        message={
          coversLast30Days
            ? "Try another range or metric. A paused check has nothing to show."
            : "Try a longer range. A paused check has nothing to show."
        }
        action={
          !coversLast30Days && (
            <Button variant="secondary" onClick={showLast30Days}>
              Show last 30 days
            </Button>
          )
        }
      />
    );
  } else if (!error) {
    // On a failed request the alert above says what went wrong; the charts' own empty states
    // would claim a check or metric is missing.
    chartBody = (
      <>
        {chartTitle && (
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{chartTitle}</span>
            {/* The seasonality heatmap is the one chart that does not zoom. */}
            {config.chartType !== "heatmap" && (
              <span className={styles.zoomHint}>
                <Icon icon={FiZoomIn} />
                Drag chart to zoom
              </span>
            )}
          </div>
        )}
        <div ref={chartRef} className={styles.chartCanvas}>
          <MetricsExplorerChart
            series={result?.series ?? []}
            analysis={result?.analysis ?? []}
            chartType={config.chartType}
            checkNames={checkNames}
            seriesLetters={legendLetters}
            granularity={result?.meta.granularity ?? config.granularity}
            start={dateRange.from}
            end={dateRange.to}
            downtime={downtime}
          />
        </div>
      </>
    );
  }

  return (
    <ContentContainer
      title={<MetricsExplorerTitle backTo={backTo} />}
      actionsBar={
        <MetricsExplorerActions
          query={queryInput}
          checkNames={checkNames}
          chartRef={chartRef}
          canSave={hasSomethingToSave}
          isUnsaved={isUnsaved}
          onSave={() => {
            if (activeView) {
              commitSave(activeView.name, activeView.id);
              return;
            }
            setNameDialog({mode: "save", defaultName: ""});
          }}
          onSaveAs={() => setNameDialog({mode: "saveAs", defaultName: `${activeView?.name ?? "Chart"} copy`})}
        />
      }
      subHeader={
        <div className={classNames("px-4 py-4 border-bottom", styles.chartHeader)}>
          <div className={styles.chartTitle}>
            <ChartNameMenu
              views={views}
              activeView={activeView}
              configOf={configOf}
              onOpen={openView}
              onNew={() => {
                setActiveView(null);
                setOpenedConfig(null);
                setConfig(createDefaultConfig());
              }}
              onRename={() => activeView && setNameDialog({mode: "rename", defaultName: activeView.name})}
              onDelete={async () => {
                if (activeView && (await deleteView(activeView.id))) {
                  setActiveView(null);
                  setOpenedConfig(null);
                }
              }}
            />
            <ChartSummary
              seriesCount={config.series.length}
              hiddenCount={config.series.filter((series) => series.hidden).length}
              lineCount={result ? result.series.length : null}
            />
          </div>

          <MetricsExplorerToolbar
            start={dateRange.from}
            end={dateRange.to}
            fixedRange={fixedRangeOption?.value}
            granularity={config.granularity}
            comparison={config.comparison}
            chartType={config.chartType}
            onGranularityChange={(granularity) => setConfig({...config, granularity})}
            onComparisonChange={(comparison) => setConfig({...config, comparison})}
            onChartTypeChange={(chartType) => setConfig({...config, chartType})}
            onRangeChange={(start, end, fixedRange) =>
              handleRangeChange(start && end ? {from: start, to: end} : null, fixedRange)
            }
          />
        </div>
      }
    >
      <div className={styles.layout}>
        <div className={styles.body}>
          <Card variant="white" className={styles.chartPanel}>
            {error && <Alert variant="error">{error.message}</Alert>}
            {result?.errors.map((seriesError) => (
              <Alert key={`${seriesError.ref}-${seriesError.code}`} variant="default">
                {seriesError.message}
              </Alert>
            ))}
            <div className={styles.chartArea}>{chartBody}</div>
            {result && (
              <div className={styles.meta}>
                <span>{result.meta.total_datapoints} points</span>
                <span>{result.meta.query_count} queries</span>
                <span>{result.meta.query_time_ms} ms</span>
                {result.meta.truncated && <span>Truncated — narrow the range or the series.</span>}
              </div>
            )}
          </Card>

          <SeriesBuilderRail
            config={config}
            schema={schema}
            checkMeta={checkMeta}
            start={dateRange.from}
            end={dateRange.to}
            onUpdateSeries={handleUpdateSeries}
            guessedFields={guessedFields}
            seriesLetters={letters}
            onAddSeries={addSeries}
            onRemoveSeries={removeSeries}
            onDuplicateSeries={duplicateSeries}
            onChecksResolved={handleChecksResolved}
          />
        </div>

        <AnalysisControls
          analysis={config.analysis}
          showDowntime={config.showDowntime}
          downtimeAvailable={downtimeCheckId !== null}
          onChange={(patch) => setConfig({...config, analysis: {...config.analysis, ...patch}})}
          onShowDowntimeChange={(showDowntime) => setConfig({...config, showDowntime})}
        />

        {result && result.analysis.length > 0 && (
          <AnalysisReadout analysis={result.analysis} series={result.series} seriesLetters={legendLetters} />
        )}

        <NavigationBlockerModal />

        <ChartNameModal
          mode={nameDialog?.mode ?? null}
          defaultName={nameDialog?.defaultName ?? ""}
          onSubmit={handleNameSubmit}
          onCancel={() => setNameDialog(null)}
        />
      </div>
    </ContentContainer>
  );
};
